import { AxiosError } from 'axios';
import { useQuery } from 'react-query';
import { GET_URL } from '@utils/api/endpoints.ts';
import { BaseError } from '@utils/types/shared/base.ts';
import { Axios } from '@utils/api/axios-instance.ts';
import { useTranslation } from 'react-i18next';

export interface PlanType {
  description: string;
  value: 'Bundle' | 'Pay as you Go' | 'Perpetual' | undefined;
}

export interface PlanBo {
  id: number;
  sku: string;
  description: string;
  type: PlanType;
  alignment_enabled: boolean;
  alignment_pricing_plan: number;
  automatic_training_enabled: boolean;
  automatic_training_monthly_cost: number;
  automatic_training_unit_cost: number;
  batch_enabled: boolean;
  cost: number | null;
  media_unit_size: number;
  minimal_invoice: number | null;
  monthly_cost: number;
  realtime_enabled: boolean;
  realtime_max_concurrent_streams?: number | null;
  realtime_languages?: string[] | null;
  stt_limit: number;
  stt_pricing_plan: number;
  translation_characters: number;
  translation_cost: number;
  translation_enabled: boolean;
  stt_templates_exported_cost: number;
  templates_exported_enabled: boolean;
  processing_type: string;
}

export interface PlanBoResponse {
  count: number;
  result: PlanBo;
  total_results: number;
}

export const useGetPlanBo = (id: string | undefined) => {
  const { t } = useTranslation();

  const fetchPlanBo = async (): Promise<PlanBoResponse> => {
    if (!id) {
      throw new Error(t('plan-id-is-not-defined'));
    }
    const response = await Axios.get<PlanBoResponse>(`${GET_URL(id).planBo}`);
    return response.data;
  };

  return useQuery<PlanBoResponse, AxiosError<BaseError>>(
    ['plan', id],
    fetchPlanBo,
    {
      enabled: !!id,
      useErrorBoundary: false
    }
  );
};
